// ─────────────────────────────────────────────────────────────
// ABA SESSION READY™ — all copy and links live here. Image URLs live in data/images.ts.
// Same structure as Team Meeting Ready (meetings.stockdigital.shop), modeled on Managers.
// Promise (must match ads, checkout and the PDF): 52-week session plan + 10 complete skill programs,
// $27 one-time, 10-day guarantee, the 5 bonuses in the checkout order.
// Values = the checkout: main $159; bonuses $37, $27, $27, $37, $27 ($155); total $314 → $27, you save $287.
// Health niche: professional material for practitioners. No outcome promises, no assessment brand names,
// respectful language ("the child", "learner"), no testimonials.
// ─────────────────────────────────────────────────────────────

export const CHECKOUT_URL = 'https://aba-session-ready.impultienda.ar/checkout';
/** Meta pixel: "pixel aba session ready" (stock 4). InitiateCheckout fires ONLY on the offer button. */
export const META_PIXEL_ID = '2154625155144842';

export const PAGE_TITLE = 'ABA Session Ready™ | Your Whole Year of Sessions, Already Planned';
export const META_DESCRIPTION =
  'A 52-week session plan plus 10 complete, ready-to-run skill programs for early learners. Open this week’s page, prepare in 5 minutes and run the session.';

// ── GLOBAL ───────────────────────────────────────────────────
export const brand = 'ABA SESSION READY™';
/** Label for every button that scrolls to the offer section */
export const ctaLabel = 'GET MY SESSIONS READY';
export const ctaMicrocopy = 'Instant PDF · One-time $27 · 10-day guarantee';
export const stickyLabel = 'GET MY SESSIONS READY · $27';
export const price = '$27';

// ── 1. HERO (buyer line, title, short subtitle, mockup, button) ──
export const hero = {
  eyebrow: 'FOR ABA THERAPISTS, RBTS & SPECIAL EDUCATION TEACHERS',
  titleLead: 'YOUR WHOLE YEAR OF SESSIONS,',
  titleHighlight: 'ALREADY PLANNED.',
  sub: 'A 52-week session plan plus 10 complete, ready-to-run skill programs. Open this week’s page, prepare in 5 minutes and run the session.',
  mockupAlt: 'ABA Session Ready™ on a desktop screen, with the 5 bonuses on tablets and phones',
};

// ── 2. STATS ─────────────────────────────────────────────────────
export const stats = [
  { value: '52', label: 'WEEKS PLANNED' },
  { value: '10', label: 'COMPLETE PROGRAMS' },
  { value: '5 MIN', label: 'TO PREPARE EACH SESSION' },
  { value: '$27', label: 'ONE-TIME PAYMENT' },
];

// ── 2b. BEFORE / AFTER ──────────────────────────────────────────────
export const beforeAfter = {
  title: 'THE NIGHT BEFORE VS. SESSION TIME.',
  before: {
    label: 'BEFORE',
    when: 'The night before',
    quote: '“What do I run in tomorrow’s session?”',
    items: ['Hours searching for activities.', 'The same table drills again.', 'A data sheet made from scratch.'],
  },
  after: {
    label: 'AFTER',
    when: 'Session time',
    items: ['Open this week’s page.', 'Prepare in 5 minutes.', 'Run the 20-minute session block.', 'Fill in the data sheet. Done.'],
  },
};

// ── 3. WHAT DOES YOUR LEARNER NEED (goal selector with an example program) ──
const block = 'Session block: warm-up 3 · table teaching 8 · natural play 7 · data & break 2';
export const selector = {
  eyebrow: 'START WHERE YOUR LEARNER IS',
  title: 'WHAT DOES YOUR LEARNER NEED THIS WEEK?',
  sub: 'Pick a goal and see the program that is ready for it.',
  startLabel: 'YOUR STARTING POINT',
  tablistLabel: 'Learner goals',
  columns: ['PAGE 1 · PLAN', 'PAGE 2 · TEACH IT', 'PAGE 3 · DATA & NEXT STEPS'],
  note: 'All 10 programs follow this same 3-page structure. Adapt each one to the child’s individual plan and your supervising BCBA.',
  items: [
    {
      tab: 'Asking for what they want',
      n: 'PROGRAM 01 · WEEK 1',
      title: 'Requesting',
      goal: 'The child asks for a wanted item or action with a word, a sign or a picture card.',
      open: ['Target skill, materials and 5-minute prep', 'Your cue, word for word: hold the item in view, wait 3 seconds, then model “Bubbles!”', block],
      activity: ['Teaching steps at the table and in natural play', 'Prompt ladder from most to least help, and error correction', 'If the child reaches and cries for the item: what you do'],
      close: ['Ready-to-print data sheet, 10 trials', 'Generalize it: new adults, the kitchen, the playground', 'Home practice note for parents and when to move on'],
    },
    {
      tab: 'Following simple directions',
      n: 'PROGRAM 03 · WEEK 9',
      title: 'Following One-Step Instructions',
      goal: 'The child follows a simple instruction like “give me”, “sit down” or “put it in”.',
      open: ['Target skill, materials and 5-minute prep', 'Your cue, word for word: get close, at the child’s level, and say once: “Put it in.”', block],
      activity: ['Teaching steps: 3 easy instructions that are part of play', 'Prompt ladder from most to least help, and error correction', 'If the child walks away: what you do'],
      close: ['Ready-to-print data sheet, 10 trials', 'Generalize it: different rooms and different adults', 'Home practice note for parents and when to move on'],
    },
    {
      tab: 'Waiting and taking turns',
      n: 'PROGRAM 07 · WEEK 27',
      title: 'Waiting & Turn-Taking',
      goal: 'The child waits calmly for a short time and takes turns with an adult, then with a peer.',
      open: ['Target skill, materials and 5-minute prep', 'Your cue, word for word: show the wait card and say “Wait… your turn!”', block],
      activity: ['Teaching steps: from a 2-second wait to “my turn, your turn” with a ramp', 'Prompt ladder from most to least help, and error correction', 'If the child grabs the toy during the wait: what you do'],
      close: ['Data sheet for waiting (seconds) and turns', 'Generalize it: snack time, waiting in line, siblings', 'Home practice note for parents and when to move on'],
    },
    {
      tab: 'A daily routine: handwashing',
      n: 'PROGRAM 09 · WEEK 35',
      title: 'Handwashing Routine',
      goal: 'The child washes their hands following a picture routine strip, with less help over time.',
      open: ['Target skill, materials and 5-minute prep', 'Your cue, word for word: point to the strip and say “Time to wash hands.”', block],
      activity: ['6-step task strip: water on, soap, rub, rinse, water off, dry', 'Prompt ladder, fading help from the last step', 'If the child plays with the water: what you do'],
      close: ['Task analysis data sheet: 6 steps, prompt level and date', 'Generalize it: home, school and the clinic bathroom', 'Home practice note for parents and when to move on'],
    },
  ],
};

// ── 4. LOOK INSIDE (real pages of the book, in the same order as pageImages) ──
export const lookInside = {
  eyebrow: 'REAL PAGES. SEE THE FORMAT BEFORE YOU BUY.',
  title: 'SEE WHAT YOU’LL HAVE IN FRONT OF YOU.',
  sub: 'Open any page and read it at full size: your 52-week plan, complete programs, data sheets and your session field card.',
  hint: 'Swipe to see more · Tap any page to view it full size',
  note: 'These are selected pages, not the full guide.',
  captions: [
    'Your 52-week plan (Q1)',
    'Program 01 · plan',
    'Program 01 · teach it',
    'Program 01 · data & next steps',
    'When the session gets hard',
    'Your session field card',
  ],
};

// ── 5. THE SYSTEM ──────────────────────────────
export const plan = {
  eyebrow: 'ONE SYSTEM FOR EVERY SESSION',
  title: 'THE WHOLE YEAR IS PLANNED. YOU JUST OPEN IT AND RUN IT.',
  text: 'Every week of the year is mapped: a skill focus, a goal and a ready 10-minute play activity, or the start of one of the 10 complete programs. Each program gives you the plan, the teaching steps and the data sheet in 3 pages.',
  steps: [
    { title: 'OPEN', text: 'Find this week in your 52-week plan.' },
    { title: 'PREPARE', text: 'Read the page and get the materials ready in 5 minutes.' },
    { title: 'RUN IT', text: 'Warm up, teach at the table, practice in natural play.' },
    { title: 'RECORD', text: 'Fill in the data sheet and send the home practice note.' },
  ],
  featuresTitle: 'IN EVERY COMPLETE PROGRAM',
  features: [
    { icon: 'goal', title: 'One clear target skill', text: 'One observable sentence, the week to start it and what the child needs first.' },
    { icon: 'timer', title: 'A 20-minute session block', text: 'Warm-up 3 min · Table teaching 8 min · Natural play 7 min · Data & break 2 min.' },
    { icon: 'steps', title: 'Teaching steps, table and play', text: 'Numbered steps, a prompt ladder from most to least help and error correction.' },
    { icon: 'questions', title: 'If the child… / You do…', text: 'Realistic session moments, with what you do and say next.' },
    { icon: 'quote', title: 'Your cue, word for word', text: 'The same short, clear words every time, plus a pro tip and a common mistake.' },
    { icon: 'action', title: 'Data sheet + home note', text: 'A ready-to-print data sheet, generalization ideas, a note for parents and when to move on.' },
  ],
} as const;

// ── 6. THE 52-WEEK PLAN ───────────────────────────────────────
export const yearPlan = {
  eyebrow: 'YOUR 52-WEEK SESSION PLAN',
  title: 'THE WHOLE YEAR, MAPPED WEEK BY WEEK.',
  sub: '★ weeks start a new program. ◆ weeks are progress reviews. Every other week has a skill focus, a goal and a ready 10-minute play activity.',
  quarters: [
    {
      q: 'Q1',
      weeks: 'WEEKS 1–13',
      name: 'BUILD THE BASICS',
      full: [
        { week: '1', label: 'START PROGRAM 01', title: 'Requesting' },
        { week: '5', label: 'START PROGRAM 02', title: 'Motor Imitation' },
        { week: '9', label: 'START PROGRAM 03', title: 'Following One-Step Instructions' },
        { week: '13', label: 'PROGRESS REVIEW', title: 'Review the data, update targets', review: true },
      ],
      activities: '+ 9 ready 10-minute play activities, like Bubble Pause, Copy My Car and Toy Clean-Up Race.',
    },
    {
      q: 'Q2',
      weeks: 'WEEKS 14–26',
      name: 'LEARN THROUGH PLAY',
      full: [
        { week: '14', label: 'START PROGRAM 04', title: 'Matching & Sorting' },
        { week: '18', label: 'START PROGRAM 05', title: 'Receptive Identification' },
        { week: '22', label: 'START PROGRAM 06', title: 'Expressive Labeling' },
        { week: '26', label: 'PROGRESS REVIEW', title: 'Review the data, update targets', review: true },
      ],
      activities: '+ 9 ready 10-minute play activities, like Sock Match, Feed the Puppet and Peek-a-Box.',
    },
    {
      q: 'Q3',
      weeks: 'WEEKS 27–39',
      name: 'PLAY TOGETHER',
      full: [
        { week: '27', label: 'START PROGRAM 07', title: 'Waiting & Turn-Taking' },
        { week: '31', label: 'START PROGRAM 08', title: 'Play Skills' },
        { week: '35', label: 'START PROGRAM 09', title: 'Handwashing Routine' },
        { week: '39', label: 'PROGRESS REVIEW', title: 'Review the data, update targets', review: true },
      ],
      activities: '+ 9 ready 10-minute play activities, like Ramp Turns, Teddy’s Picnic and Picture Strip.',
    },
    {
      q: 'Q4',
      weeks: 'WEEKS 40–52',
      name: 'CONNECT AND GENERALIZE',
      full: [
        { week: '40', label: 'START PROGRAM 10', title: 'Greetings & Peer Interaction' },
        { week: '52', label: 'PROGRESS REVIEW', title: 'Year-end review', review: true },
      ],
      activities: '+ 11 ready 10-minute play activities, like Hello Door, Snack Shop and Show & Share.',
    },
  ],
  toolsTitle: 'PLUS YOUR SESSION TOOLS',
  tools: ['When the session gets hard: 8 situations, what to say and do', 'Session planner: plan any session in 5 minutes', 'Session field card to keep on the table'],
};

// ── 7. THE 10 COMPLETE PROGRAMS ────────────────────────────────
export const meetings = {
  eyebrow: 'THE 10 COMPLETE PROGRAMS',
  title: 'TEN EARLY-LEARNER PROGRAMS. READY TO RUN.',
  sub: 'Follow the 52-week plan, or start with the program your learner needs now. Each one is 3 pages: plan, teach it, data & next steps.',
  weekLabel: 'START WEEK',
  items: [
    { n: '01', week: '1', title: 'Requesting', goal: 'The child asks for a wanted item or action with a word, a sign or a picture card.' },
    { n: '02', week: '5', title: 'Motor Imitation', goal: 'The child copies a simple action with a toy or the body right after you show it.' },
    { n: '03', week: '9', title: 'Following One-Step Instructions', goal: 'The child follows a simple instruction like “give me”, “sit down” or “put it in”.' },
    { n: '04', week: '14', title: 'Matching & Sorting', goal: 'The child matches identical objects and pictures, and sorts items by color.' },
    { n: '05', week: '18', title: 'Receptive Identification', goal: 'The child touches or gives the item you name, from a group of 2 or 3.' },
    { n: '06', week: '22', title: 'Expressive Labeling', goal: 'The child names familiar objects and pictures with a word, a sign or a picture card.' },
    { n: '07', week: '27', title: 'Waiting & Turn-Taking', goal: 'The child waits calmly for a short time and takes turns with an adult, then with a peer.' },
    { n: '08', week: '31', title: 'Play Skills', goal: 'The child plays with toys in more than one way and in simple pretend play.' },
    { n: '09', week: '35', title: 'Handwashing Routine', goal: 'The child washes their hands following a 6-step picture strip, with less help over time.' },
    { n: '10', week: '40', title: 'Greetings & Peer Interaction', goal: 'The child greets familiar people and plays a short, simple game with a peer.' },
  ],
  cta: 'GET ALL 10 PROGRAMS + 5 BONUSES',
  ctaMicrocopy: '$27 USD · One-time payment',
};

// ── 8. BONUSES (same order and values as the checkout) ──────────────
export const bonuses = {
  eyebrow: '5 BONUSES INCLUDED IN YOUR $27 GUIDE',
  title: 'EVERYTHING ELSE YOUR SESSIONS NEED.',
  sub: 'Play ideas, data sheets, visual supports, parent communication and language activities, ready to use.',
  free: 'FREE',
  totalNote: '$155 in bonuses, included free',
  items: [
    {
      label: 'BONUS 01',
      value: '$37',
      short: '100 Naturalistic Play Activities',
      hook: 'Never run out of play ideas.',
      title: '100 Naturalistic Play Activities — Ready to Use in Any Session',
      text: '100 play-based activities organized by goal: requesting, imitation, joint attention, turn-taking, labeling and following directions. Each one lists materials you already have, how to set up the play and how to make it easier or harder.',
    },
    {
      label: 'BONUS 02',
      value: '$27',
      short: 'Printable Data Sheets Pack',
      hook: 'Stop making data sheets from scratch.',
      title: 'Printable Data Sheets Pack — Trial, Probe & Task Analysis Sheets',
      text: 'Trial-by-trial and cold probe sheets, prompt level tracking, task analysis, frequency and duration, ABC data, a weekly progress graph and a session notes template. Clean layouts, quick to fill in.',
    },
    {
      label: 'BONUS 03',
      value: '$27',
      short: 'Visual Supports Kit',
      hook: 'Print, laminate and use them today.',
      title: 'Visual Supports Kit — Schedules, First-Then & Choice Boards',
      text: 'Visual schedules, first-then boards, choice boards, token boards, a waiting card, break cards, routine strips for handwashing and getting dressed, and emotion cards.',
    },
    {
      label: 'BONUS 04',
      value: '$37',
      short: 'Parent Update Scripts & Home Practice Sheets',
      hook: 'Know what to say to parents.',
      title: 'Parent Update Scripts & Home Practice Sheets',
      text: 'Scripts for sharing progress, explaining a new goal, talking about a hard week and setting home practice, plus a one-page home practice sheet for each of the 10 programs, in plain, warm language.',
    },
    {
      label: 'BONUS 05',
      value: '$27',
      short: '30 Speech & Language Activities',
      hook: 'Support communication in every session.',
      title: '30 Speech & Language Activities to Use Alongside ABA',
      text: 'Sound and word imitation games, early vocabulary routines, yes and no practice, simple questions, picture-based requesting and songs with pauses. Each with the goal, the materials and the steps.',
    },
  ],
};

// ── 9. OFFER ─────────────────────────────────────────────────────
export const offer = {
  eyebrow: 'YOUR NEXT SESSION STARTS HERE',
  title: 'YOUR WHOLE YEAR OF SESSIONS. ALL IN ONE GUIDE.',
  kicker: 'THE COMPLETE DIGITAL GUIDE',
  name: 'ABA Session Ready™',
  description: 'A 52-week session plan and 10 complete, ready-to-run skill programs, plus five bonuses: play activities, data sheets, visual supports, parent scripts and language activities.',
  mockupAlt: 'Everything included in ABA Session Ready™',
  includedTitle: 'YOUR GUIDE INCLUDES',
  included: [
    '52-week session plan: the whole year mapped',
    '10 complete skill programs, 3 pages each',
    'Target skill, materials and 5-minute prep',
    'A 20-minute session block for every program',
    'Teaching steps, prompt ladder and error correction',
    'Printable data sheet and home practice note in each',
    'Session tools: hard moments, planner, field card',
  ],
  /** Value stack: exactly the checkout prices */
  stackTitle: 'EVERYTHING YOU GET',
  mainItem: { label: 'ABA Session Ready™ (52-week plan + 10 complete programs)', value: '$159' },
  totalLabel: 'Total value',
  totalValue: '$314',
  todayLabel: 'TODAY',
  priceNote: 'One-time payment · About 52¢ a week',
  saveNote: 'You save $287',
  /** The ONLY button that goes to checkout */
  cta: 'GET INSTANT ACCESS — $27',
  ctaNote: ['Continue to secure checkout', 'Access sent by email after payment'],
  format: 'English-language PDF. Digital delivery. Professional material for practitioners: adapt every program to each child’s individual plan and your supervising BCBA.',
  guaranteeNote: '10-day money-back guarantee',
};

// ── 10. GUARANTEE ────────────────────────────────────────────
export const guarantee = {
  sealTop: '10-DAY',
  sealBottom: 'MONEY-BACK',
  title: '10-DAY MONEY-BACK. NO QUESTIONS ASKED.',
  text: "Use it in your next sessions. If it isn't right for you, email support@stockdigital.shop within 10 days of purchase and we'll give your money back.",
};

// ── 11. FAQ ───────────────────────────────────────────────────
export const faq = {
  eyebrow: 'KNOW EXACTLY WHAT YOU’RE GETTING',
  title: 'A FEW QUESTIONS BEFORE YOU GET STARTED.',
  sub: 'Built for ABA therapists, RBTs, BCBAs, special education teachers, SLPs and OTs who work with young learners. A ready-to-use session guide, not a video course.',
  items: [
    {
      q: 'What exactly is this product?',
      a: 'An English-language PDF guide: a 52-week session plan (every week has a skill focus, a goal and a ready 10-minute play activity, or the start of a program) and 10 complete skill programs of 3 pages each, plus session tools and 5 bonuses.',
    },
    {
      q: 'Who is it for?',
      a: 'Trained practitioners who work with young autistic learners, about 2 to 8 years old: RBTs, ABA therapists, BCBAs, special education teachers and paraprofessionals, speech-language pathologists and occupational therapists.',
    },
    {
      q: 'Does it replace the child’s plan or my BCBA?',
      a: 'No. It is professional session material, not a treatment plan, an assessment or medical advice. Every program is a starting point: adapt it to each child’s individual plan and your supervising BCBA or clinical team.',
    },
    {
      q: 'What approach do the programs follow?',
      a: 'Child-led, play-based and assent-based: follow the child’s interests, pair with fun first, offer breaks, use the lightest prompt and fade it fast. No punishment and no forcing a response.',
    },
    {
      q: 'How do I receive it?',
      a: 'After your payment is approved, access is sent to the email address you enter at checkout. Nothing is shipped. Use an email you can access and check your spam folder if needed.',
    },
    {
      q: 'How long is each session block?',
      a: 'Programs 01 to 09 use a 20-minute block: warm-up 3, table teaching 8, natural play 7, data & break 2. Program 10 uses a 25-minute block with peer practice. On the other weeks, the 10-minute play activity fits into your usual session.',
    },
    {
      q: 'Can I use the programs individually?',
      a: 'Yes. Each program stands alone, so you can start with the one your learner needs now, or follow the 52-week plan from week 1.',
    },
    {
      q: 'Can I print it?',
      a: 'Yes. The pages are easy to read on a phone or tablet during the session, and the data sheets and the session planner are ready to print.',
    },
    {
      q: 'Can I share it with my team or clinic?',
      a: 'This purchase is for your own use as a practitioner. Sharing it across a clinic or school, redistributing it or reselling it is not included in this offer.',
    },
    { q: 'Is it a subscription?', a: 'No. One payment of $27 USD.' },
    { q: 'How does the guarantee work?', a: 'You have 10 days to request a refund, no questions asked.' },
  ],
};

// ── 12. FINAL CTA ─────────────────────────────────────────────
export const finalCta = {
  eyebrow: 'FOR THE NEXT “WHAT DO I RUN IN TOMORROW’S SESSION?”',
  title: 'HAVE YOUR NEXT SESSION READY IN 5 MINUTES.',
  text: 'Open this week’s page. Prepare in 5 minutes. Run the session with a plan already in place.',
  microcopy: '52-week plan + 10 complete programs + 5 bonuses · $27 USD, one-time payment',
};

// ── 13. FOOTER ──────────────────────────────────────────────────
export const footer = {
  line: 'ABA SESSION READY™ · Professional session material for practitioners. Not medical advice or a treatment plan.',
  supportLabel: 'Support:',
  supportEmail: 'support@stockdigital.shop',
  copyright: '© 2026',
};
